import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormControlLabel,
  FormGroup,
  Skeleton,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { RuleInstance } from "../../../types/models/RuleInstance";
import { ContentRevision } from "../../../types/models/ContentRevision";
import { AppState } from "../../../redux/store";
import { RuleUnitState } from "../../../types/models/RuleUnitState";
import { ChatState } from "../../../types/models/ChatState";
import { AlertViewMode } from "../../../constants/AppEnums";
import { getRuleInstanceMetadata } from "../../../utils/RuleUtil";
import AppCard from "../../../components/AppCard";
import EmptyResult from "../../rules/EmptyResult";
import MessageItem from "../../../components/Chat/MessageItem";
import RuleInstanceList, {
  RuleInstanceTargetItem,
} from "../../rules/ContentRevisionView/RuleInstanceList";
import { PostAcknowledgedRequest } from "../../../types/models/PostAcknowledgedRequest";
import { getMyProgressDetail, postAcknowledgeStatus } from "../../../redux/actions";

interface AlertsCardProps {
  handleSelectAlert: (rule: RuleInstance, content: ContentRevision) => void;
  handleSelectViewMode: (viewMode: string) => void;
  targetItem?: RuleInstanceTargetItem;
  viewMode: string;
  alerts: { alerts: RuleInstance[]; history: RuleInstance[] };
}

export default function AlertsCard(props: AlertsCardProps) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const chatState = useSelector<AppState, ChatState>((state) => state.chat);

  const [displayRules, setDisplayRules] = useState<RuleInstance[]>();

  useEffect(() => {
    if (props.viewMode === AlertViewMode.Alert) {
      setDisplayRules(props.alerts.alerts);
    } else {
      setDisplayRules(props.alerts.history);
    }
  }, [props.alerts.alerts, props.alerts.history, props.viewMode]);

  const getDisplayAlerts = () => {
    const handleSelectAlert = (selectedRule: RuleInstance) => {
      const contentRevision = ruleUnitState.baseAlertContentRevisionDetails?.find((detail) =>
        detail.RuleInstances?.some((rule) => rule.RuleInstanceId === selectedRule.RuleInstanceId)
      );
      if (contentRevision) {
        props.handleSelectAlert(selectedRule, contentRevision);
        if (!selectedRule.ReadAt) {
          const request: PostAcknowledgedRequest = {
            AcknowledgementType: "read",
            DeviceId: null,
            RuleInstanceId: selectedRule.RuleInstanceId,
            UsersTime: new Date(),
          };
          dispatch(postAcknowledgeStatus(request, ()=> {
            // Update "Read" status of alert list
            dispatch(getMyProgressDetail());
          }));
        }
      }
    };

    if (
      (props.viewMode === AlertViewMode.Alert &&
        (props.alerts.alerts === undefined || ruleUnitState.myAlerts === undefined)) ||
      (props.viewMode === AlertViewMode.History && props.alerts.history === undefined) ||
      !displayRules
    ) {
      // The API is not done yet
      return (
        <Box p={2} display="flex" flexDirection="column" gap={4}>
          <Skeleton variant="rounded" width="100%" height="1em" />
          <Skeleton variant="rounded" width="100%" height="1em" />
          <Skeleton variant="rounded" width="100%" height="1em" />
          <Skeleton variant="rounded" width="100%" height="1em" />
          <Skeleton variant="rounded" width="100%" height="1em" />
          <Skeleton variant="rounded" width="100%" height="1em" />
        </Box>
      );
    }

    if (
      ruleUnitState.searchText &&
      ruleUnitState.searchText?.length > 0 &&
      ((props.viewMode === AlertViewMode.Alert && props.alerts.alerts?.length === 0) ||
        (props.viewMode === AlertViewMode.History && props.alerts.history.length === 0))
    ) {
      // No search result exists
      return <EmptyResult>No items matching your current search.</EmptyResult>;
    }

    if (
      ruleUnitState.searchText &&
      ruleUnitState.searchText?.length > 0 &&
      ((props.viewMode === AlertViewMode.Alert && props.alerts.alerts.length === 0) ||
        (props.viewMode === AlertViewMode.History && props.alerts.history.length === 0))
    ) {
      // No search result exists
      return <EmptyResult>No items matching your current search.</EmptyResult>;
    }

    // Display the alerts
    return (
      <Box>
        <RuleInstanceList
          alerts={props.alerts}
          narrowRowSpace={true}
          ruleInstances={displayRules}
          onViewRuleDetail={(rule) => handleSelectAlert(rule)}
          secondaryTextFunction={(rule: RuleInstance) =>
            getRuleInstanceMetadata(rule.Metadata ?? "")?.annotations?.join(", ")
          }
          footerElement={(rule: RuleInstance) => {
            const thread = chatState.chatThreads?.find(
              (thread) => thread.ruleInstanceId === rule.RuleInstanceId
            );
            if (thread && thread.preview) {
              return (
                <MessageItem
                  smallFontSize={true}
                  message={{
                    Message: thread.preview,
                    MessageId: "",
                    SentAt: "",
                    UserDisplayName: "",
                    UserId: "",
                    RuleInstanceId: thread.ruleInstanceId,
                  }}
                  hideSenderAndTime={false}
                  isLast={true}
                  senderId={ruleUnitState.userShieldId}
                />
              );
            }
          }}
          targetItem={props.targetItem}
        />
      </Box>
    );
  };

  return (
    <Box component="section" id="alerts">
      <AppCard
        contentStyle={{ px: 0 }}
        title={
          <Box
            display="flex"
            gap={0.5}
            p={0.5}
            alignItems="center"
            sx={{
              color: theme.card.headerTextColor,
              fontWeight: 600,
              fontSize: 18,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
              margin: 0,
              display: "flex",
            }}
          >
            <NotificationsNoneIcon />
            <Typography variant="h5" component="h3">
              Alerts
            </Typography>
          </Box>
        }
        headerStyle={{ backgroundColor: theme.card.headerBgColor }}
        action={
          <Box
            sx={{
              marginTop: "4px",
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
            }}
          >
            <FormGroup>
              <FormControlLabel
                labelPlacement="end"
                control={
                  <Switch
                    checked={props.viewMode === AlertViewMode.History}
                    onChange={(event) => {
                      const newViewMode = event.target.checked
                        ? AlertViewMode.History
                        : AlertViewMode.Alert;
                      props.handleSelectViewMode(newViewMode);
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 15 }}>History</Typography>}
              />
            </FormGroup>
          </Box>
        }
      >
        <Box>{getDisplayAlerts()}</Box>
      </AppCard>
    </Box>
  );
}
